import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { useProducts } from '../ProductContext';
import { useDamagedProducts } from '../DamagedProductsContext';
import { apiJson } from '../apiClient';
import { canModerateBranch } from '../utils/userLocale';
import { isAutomotiveBusiness } from '../utils/businessType';
import './DamagedProductsPage.css';

function DamagedProductsPage() {
  const { user } = useAuth();
  const automotive = isAutomotiveBusiness(user);
  const { products, inventoryLoading, refreshProducts } = useProducts();
  const { refreshDamaged } = useDamagedProducts();

  const [productId, setProductId] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [damageDate, setDamageDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [quantity, setQuantity] = useState(1);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [fileKey, setFileKey] = useState(0);

  const [allItems, setAllItems] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const selectedProduct = useMemo(
    () => products.find((p) => String(p.id) === String(productId)),
    [products, productId]
  );
  const stock = selectedProduct ? Number(selectedProduct.quantity) || 0 : 0;

  const fetchItems = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const data = await apiJson('/damaged-products');
      const items = Array.isArray(data.items) ? data.items : [];
      setAllItems(items);
      await refreshDamaged();
    } catch (e) {
      setListError(e.body?.message || e.message || 'Could not load damage records.');
      setAllItems([]);
    } finally {
      setListLoading(false);
    }
  }, [refreshDamaged]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const pending = useMemo(() => allItems.filter((i) => i.status === 'pending'), [allItems]);
  const history = useMemo(() => allItems.filter((i) => i.status !== 'pending'), [allItems]);

  const resetForm = () => {
    setProductId('');
    setDescription('');
    setReason('');
    setDamageDate(new Date().toISOString().slice(0, 10));
    setQuantity(1);
    setImage(null);
    setImagePreview(null);
    setFileKey((k) => k + 1);
  };

  const handleProductChange = (e) => {
    const v = e.target.value;
    setProductId(v);
    const p = products.find((x) => String(x.id) === v);
    const st = p ? Number(p.quantity) || 0 : 0;
    setQuantity((q) => {
      if (st < 1) return 1;
      return Math.min(Math.max(1, Number(q) || 1), st);
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    if (!productId) {
      setError('Select a product from stock.');
      return;
    }
    if (stock < 1) {
      setError('This product has no stock left to report as damaged.');
      return;
    }
    if (!quantity || quantity < 1 || quantity > stock) {
      setError(`Enter a quantity between 1 and ${stock} (current stock).`);
      return;
    }
    if (!reason) {
      setError('Enter the reason for damage.');
      return;
    }
    if (!damageDate) {
      setError('Select the date of the incident.');
      return;
    }
    if (image && image.size > 2 * 1024 * 1024) {
      setError('Image must be 2MB or smaller.');
      return;
    }
    if (image && !image.type.startsWith('image/')) {
      setError('Please choose an image file only.');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('product_id', String(productId));
      fd.append('description', description || '');
      fd.append('reason', reason);
      fd.append('damage_date', damageDate);
      fd.append('quantity', String(quantity));
      if (image) fd.append('image', image);

      await apiJson('/damaged-products', { method: 'POST', body: fd });
      resetForm();
      await fetchItems();
      await refreshProducts();
    } catch (err) {
      setError(err.body?.message || err.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBossAction = async (id, approved) => {
    setActionError('');
    setProcessingId(id);
    try {
      await apiJson(`/damaged-products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(
          approved ? { status: 'approved' } : { status: 'rejected', rejection_reason: null }
        ),
      });
      await fetchItems();
      await refreshProducts();
    } catch (err) {
      setActionError(err.body?.message || err.message || 'Action failed.');
    } finally {
      setProcessingId(null);
    }
  };

  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    [products]
  );

  const cardImageSrc = (item) => item.imageUrl || item.image || null;

  return (
    <div className="damaged-products-page page-bleed">
      <header className="damaged-page-header">
        <h1>{automotive ? 'Damaged spare parts' : 'Damaged Products'}</h1>
        <p className="damaged-page-lead">
          {automotive
            ? 'Chagua spare part iliyopo kwenye stock. Owner au manager anaidhinisha; ukikubaliwa, idadi hiyo inapunguzwa kwenye inventory.'
            : 'Choose a product that is already in stock. The owner or manager approves each report; when approved, that quantity is deducted from inventory. Optional notes and photo.'}
        </p>
      </header>

      <form className="damaged-product-form" onSubmit={handleAdd}>
        <div className="damaged-form-grid">
          <div className="damaged-field">
            <label htmlFor="damaged-product-select">{automotive ? 'Spare part (from stock)' : 'Product (from stock)'}</label>
            <select
              id="damaged-product-select"
              value={productId}
              onChange={handleProductChange}
              required
              disabled={inventoryLoading}
            >
              <option value="">{inventoryLoading ? 'Loading products…' : 'Select product…'}</option>
              {sortedProducts.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                  {typeof p.quantity === 'number' ? ` (${p.quantity} in stock)` : ''}
                </option>
              ))}
            </select>
            {!inventoryLoading && sortedProducts.length === 0 && (
              <p className="damaged-stock-hint">No approved products in stock yet. Add products first.</p>
            )}
            {selectedProduct && (
              <p className="damaged-stock-hint">
                Current stock: <strong>{stock}</strong>
                {stock < 1 ? ' — cannot submit a damage report until stock is available.' : ''}
              </p>
            )}
          </div>

          <div className="damaged-field">
            <label htmlFor="damaged-description">
              Description <span className="damaged-field-hint">(optional)</span>
            </label>
            <textarea
              id="damaged-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Condition or other details…"
            />
          </div>

          <div className="damaged-field">
            <label htmlFor="damaged-reason">Reason for damage</label>
            <input
              id="damaged-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. broken in transit"
              required
            />
          </div>

          <div className="damaged-form-row--split">
            <div className="damaged-field">
              <label htmlFor="damaged-date">Date of incident</label>
              <input
                id="damaged-date"
                type="date"
                value={damageDate}
                onChange={(e) => setDamageDate(e.target.value)}
                required
              />
            </div>
            <div className="damaged-field">
              <label htmlFor="damaged-qty">Quantity damaged</label>
              <input
                id="damaged-qty"
                type="number"
                min={1}
                max={stock >= 1 ? stock : undefined}
                value={quantity}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isFinite(n)) return;
                  const cap = stock >= 1 ? stock : 999999;
                  setQuantity(Math.min(Math.max(1, n), cap));
                }}
                required
                disabled={!productId || stock < 1}
              />
            </div>
          </div>

          <div className="damaged-field damaged-file-wrap">
            <span id="damaged-file-label-text">Photo (evidence)</span>
            <span className="damaged-field-hint">Optional · JPG/PNG · up to 2MB</span>
            <input
              key={fileKey}
              className="damaged-file-input"
              id="damaged-file"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              aria-labelledby="damaged-file-label-text"
            />
            <label className="damaged-file-label" htmlFor="damaged-file">
              Choose image file
            </label>
            {image?.name && <span className="damaged-file-name">{image.name}</span>}
            {imagePreview && (
              <div className="damaged-image-preview">
                <img src={imagePreview} alt="Preview" />
              </div>
            )}
          </div>
        </div>

        {error && <div className="damaged-form-error" role="alert">{error}</div>}

        <div className="damaged-form-actions">
          <button
            type="submit"
            className="damaged-btn-submit"
            disabled={submitting || inventoryLoading || !productId || stock < 1}
          >
            {submitting ? 'Submitting…' : automotive ? 'Submit damaged spare part' : 'Submit damaged product'}
          </button>
        </div>
      </form>

      <div className="damaged-products-lists">
        {listLoading ? (
          <p className="damaged-list-loading">Loading records…</p>
        ) : (
          <>
            {listError && (
              <div className="damaged-form-error" role="alert" style={{ marginBottom: 16 }}>
                {listError}
              </div>
            )}

            {actionError && (
              <div className="damaged-form-error" role="alert" style={{ marginBottom: 16 }}>
                {actionError}
              </div>
            )}

            <section aria-labelledby="damaged-pending-heading">
          <h2 id="damaged-pending-heading" className="damaged-section-title">
            Pending confirmation
          </h2>
          {pending.length === 0 ? (
            <p className="damaged-empty">No items awaiting confirmation.</p>
          ) : (
            <ul className="damaged-card-list">
              {pending.map((p) => (
                <li key={p.id} className="damaged-card">
                  <p className="damaged-card-title">{p.name}</p>
                  <div className="damaged-card-meta">
                    <div>
                      <strong>Quantity:</strong> {p.quantity}
                      {p.description ? (
                        <>
                          {' · '}
                          <strong>Description:</strong> {p.description}
                        </>
                      ) : null}
                    </div>
                    <div>
                      <strong>Reason:</strong> {p.reason}
                    </div>
                    <div>
                      <strong>Date:</strong> {p.damageDate}
                    </div>
                    {p.addedByName && (
                      <div>
                        <strong>Reported by:</strong> {p.addedByName}
                      </div>
                    )}
                  </div>
                  {cardImageSrc(p) && (
                    <div className="damaged-card-thumb">
                      <img src={cardImageSrc(p)} alt="" />
                    </div>
                  )}
                  <span className="damaged-badge damaged-badge--pending">Pending</span>
                  {canModerateBranch(user?.role) && (
                    <div className="damaged-card-actions">
                      <button
                        type="button"
                        className="damaged-btn-approve"
                        disabled={processingId === p.id}
                        onClick={() => handleBossAction(p.id, true)}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="damaged-btn-reject"
                        disabled={processingId === p.id}
                        onClick={() => handleBossAction(p.id, false)}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-labelledby="damaged-history-heading">
          <h2 id="damaged-history-heading" className="damaged-section-title">
            History
          </h2>
          {history.length === 0 ? (
            <p className="damaged-empty">No approved or rejected items yet.</p>
          ) : (
            <ul className="damaged-card-list">
              {history.map((h) => (
                <li key={h.id} className="damaged-card">
                  <p className="damaged-card-title">{h.name}</p>
                  <div className="damaged-card-meta">
                    <div>
                      <strong>Quantity:</strong> {h.quantity}
                      {h.description ? (
                        <>
                          {' · '}
                          <strong>Description:</strong> {h.description}
                        </>
                      ) : null}
                    </div>
                    <div>
                      <strong>Reason:</strong> {h.reason}
                    </div>
                    <div>
                      <strong>Date:</strong> {h.damageDate}
                    </div>
                  </div>
                  {cardImageSrc(h) && (
                    <div className="damaged-card-thumb">
                      <img src={cardImageSrc(h)} alt="" />
                    </div>
                  )}
                  <span
                    className={
                      h.status === 'approved'
                        ? 'damaged-badge damaged-badge--approved'
                        : 'damaged-badge damaged-badge--rejected'
                    }
                  >
                    {h.status === 'approved' ? 'Approved' : 'Rejected'}
                  </span>
                  {h.status === 'approved' && (
                    <p className="damaged-boss-msg">Stock was reduced by {h.quantity} for this product.</p>
                  )}
                  {h.status === 'rejected' && h.rejectionReason && (
                    <p className="damaged-boss-msg">{h.rejectionReason}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
          </>
        )}
      </div>
    </div>
  );
}

export default DamagedProductsPage;
